import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { StorageKeys } from '@tw/lib';
import { UserService } from '@tw/services';
import { PrimeIcons } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Button } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ManagerApprovalService } from '../../services/managerapproval.services';
import { TrackerService } from '../../services/tracker.service';
import { SidebarService } from '../sidebar/sidebar.service';

interface PendingSubmission {
  userId: number;
  userName: string;
  /** Unique submission dates as YYYY-MM-DD, sorted ascending. */
  dates: string[];
  latestSubmittedOn: string;
}

interface RejectedEntry {
  taskDate: string;
  comments: string;
  approvedByName: string;
  responsesUpdatedOn: string;
}

const POLL_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const LOOKBACK_DAYS = 15;

@Component({
  selector: 'tw-topbar',
  imports: [Button, Avatar, FormsModule, MenuModule, TooltipModule],
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
})
export class TopbarComponent implements OnInit, OnDestroy {
  protected PrimeIcons = PrimeIcons;
  protected userService = inject(UserService);
  protected sidebarService = inject(SidebarService);
  protected sidebarService1 = inject(SidebarService);
  private managerApprovalService = inject(ManagerApprovalService);
  private trackerService = inject(TrackerService);

  constructor(private router: Router) {}

  items: any[] = [];
  protected initials = computed(() => {
    const user = this.userService.userDetails();
    const firstInitial = user?.firstName?.getFirstCharsFromWords?.() || '';
    const lastInitial = user?.lastName?.getFirstCharsFromWords?.() || '';
    return `${firstInitial}${lastInitial}`;
  });

  pendingSubmissions = signal<PendingSubmission[]>([]);
  rejectedEntries = signal<RejectedEntry[]>([]);

  isManager = computed(() => !!this.userService.userDetails()?.isReportingManager);

  /** Distinct submitters awaiting this manager's approval. */
  pendingApprovalCount = computed(() => this.pendingSubmissions().length);
  /** Total rejected days for this user. */
  rejectedDayCount = computed(() => this.rejectedEntries().length);

  totalNotificationCount = computed(
    () => this.pendingApprovalCount() + this.rejectedDayCount(),
  );

  /** When the user has both kinds of notifications, sections become collapsible. */
  bothSectionsVisible = computed(
    () => this.pendingApprovalCount() > 0 && this.rejectedDayCount() > 0,
  );

  pendingExpanded = signal(true);
  rejectedExpanded = signal(true);

  approvalsPanelOpen = signal(false);
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  isDarkMode = false;
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  ngOnInit() {
    this.isDarkMode = localStorage.getItem(StorageKeys.Local.Theme) === 'dark';
    if (this.isDarkMode) {
      document.querySelector('html')?.classList.add('tw-dark');
    }
    this.refreshNotifications();
    this.pollHandle = setInterval(
      () => this.refreshNotifications(),
      POLL_INTERVAL_MS,
    );
  }

  ngOnDestroy(): void {
    if (this.pollHandle) clearInterval(this.pollHandle);
    if (this.hideTimer) clearTimeout(this.hideTimer);
  }

  private async refreshNotifications(): Promise<void> {
    await Promise.all([
      this.loadRejectedEntries(),
      this.isManager() ? this.loadPendingApprovals() : Promise.resolve(),
    ]);
  }

  private dateRange(): { startDate: string; endDate: string } {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - LOOKBACK_DAYS);
    return { startDate: toYmd(start), endDate: toYmd(end) };
  }

  async loadPendingApprovals(): Promise<void> {
    try {
      const response = await this.managerApprovalService.getTimeEntries({
        ...this.dateRange(),
        statuses: ['Submitted'],
      });

      const byUser = new Map<number, PendingSubmission>();
      for (const row of response || []) {
        let entry = byUser.get(row.submittedById);
        if (!entry) {
          entry = {
            userId: row.submittedById,
            userName: row.submittedBy,
            dates: [],
            latestSubmittedOn: row.submittedOn,
          };
          byUser.set(row.submittedById, entry);
        }
        if (row.taskDate && !entry.dates.includes(row.taskDate)) {
          entry.dates.push(row.taskDate);
        }
        if (row.submittedOn > entry.latestSubmittedOn) {
          entry.latestSubmittedOn = row.submittedOn;
        }
      }
      const list = Array.from(byUser.values()).map((u) => ({
        ...u,
        dates: u.dates.sort(),
      }));
      list.sort((a, b) => b.latestSubmittedOn.localeCompare(a.latestSubmittedOn));
      this.pendingSubmissions.set(list);
    } catch {
      this.pendingSubmissions.set([]);
    }
  }

  async loadRejectedEntries(): Promise<void> {
    try {
      const { startDate, endDate } = this.dateRange();
      const response = await this.trackerService.getApprovalTrack(
        startDate,
        endDate,
        ['Rejected'],
      );
      const list: RejectedEntry[] = (response || []).map((r) => ({
        taskDate: r.taskDate,
        comments: r.comments || '',
        approvedByName: r.approvedByName || '',
        responsesUpdatedOn: r.responsesUpdatedOn || '',
      }));
      // Most-recently rejected first
      list.sort((a, b) =>
        (b.responsesUpdatedOn || '').localeCompare(a.responsesUpdatedOn || ''),
      );
      this.rejectedEntries.set(list);
    } catch {
      this.rejectedEntries.set([]);
    }
  }

  toggleSidebar() {
    this.sidebarService.visible.set(!this.sidebarService.visible());
  }

  openUserMenu() {
    this.router.navigate(['/app/my-info']);
  }

  showApprovalsPanel(): void {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    this.approvalsPanelOpen.set(true);
  }

  /** Hide after a short delay so the mouse can travel from the bell to the popover without it closing. */
  scheduleHideApprovalsPanel(): void {
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      this.approvalsPanelOpen.set(false);
      this.hideTimer = null;
    }, 200);
  }

  goToMyTeam(): void {
    this.approvalsPanelOpen.set(false);
    this.router.navigate(['/app/my-team']);
  }

  togglePending(): void {
    this.pendingExpanded.update((v) => !v);
  }

  toggleRejected(): void {
    this.rejectedExpanded.update((v) => !v);
  }

  formatDateRange(dates: string[]): string {
    if (!dates.length) return '';
    if (dates.length === 1) return formatShort(dates[0]);
    if (dates.length <= 3) return dates.map(formatShort).join(', ');
    return `${formatShort(dates[0])} – ${formatShort(dates[dates.length - 1])} (${dates.length} days)`;
  }

  formatShortDate(date: string): string {
    return formatShort(date);
  }

  toggleTheme() {
    const html = document.querySelector('html');
    if (html) {
      html.classList.toggle('tw-dark');
      this.isDarkMode = html.classList.contains('tw-dark');
      localStorage.setItem(
        StorageKeys.Local.Theme,
        this.isDarkMode ? 'dark' : 'light',
      );
    }
  }
}

function toYmd(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

function formatShort(dateStr: string): string {
  // dateStr can be "2026-05-19" or a full ISO timestamp — first 10 chars is the date.
  const ymd = dateStr.slice(0, 10);
  const [y, m, d] = ymd.split('-');
  if (!y || !m || !d) return dateStr;
  return `${m}-${d}-${y.slice(-2)}`;
}
